// ============================================================================
// FX RATES DIALOG — system admins set the fixed reporting rates (EUR per 1
// unit) used for the ≈ EUR headline totals on the Studio CRM screen.
// ============================================================================

import React, { useState } from 'react';
import { AlertCircle, Check, Loader, X } from 'lucide-react';
import { toast } from 'sonner';
import { CrmFxRates, setFxRates } from '../../services/studio-crm';
import { formatDate } from './CrmDrawer';

const CURRENCIES = ['GBP', 'USD', 'CAD'] as const;

interface Props {
  current: CrmFxRates | null;
  onClose: () => void;
  onSaved: (fx: CrmFxRates) => void;
}

export default function FxRatesDialog({ current, onClose, onSaved }: Props) {
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(CURRENCIES.map(c => [c, current?.rates[c] !== undefined ? String(current.rates[c]) : '']))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const rates: CrmFxRates['rates'] = {};
    for (const c of CURRENCIES) {
      if (!values[c].trim()) continue;
      const n = Number(values[c]);
      if (!Number.isFinite(n) || n <= 0) {
        setError(`${c} rate must be a positive number`);
        return;
      }
      rates[c] = n;
    }
    setSaving(true);
    setError(null);
    try {
      onSaved(await setFxRates(rates));
      toast.success('Exchange rates saved');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save exchange rates');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden shadow-xl font-display"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-lg font-bold text-navy">Reporting exchange rates</h2>
            <p className="text-xs text-slate-500">Used only for the ≈ EUR totals. Deals keep their own currency.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        {error && (
          <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        <div className="p-5 flex flex-col gap-3">
          {CURRENCIES.map(c => (
            <label key={c} className="flex items-center gap-3">
              <span className="w-20 text-sm font-semibold text-navy">1 {c} =</span>
              <input type="number" min={0} step="any" value={values[c]} placeholder="not set"
                onChange={e => setValues(v => ({ ...v, [c]: e.target.value }))}
                className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
              <span className="text-sm text-slate-500">EUR</span>
            </label>
          ))}
          <p className="text-xs text-slate-400">
            Use Finance's planning rates so totals stay comparable over time.
            {current && <> Last updated by {current.updatedByName}, {formatDate(current.updatedAt.slice(0, 10))}.</>}
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          <button onClick={onClose}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
            {saving ? <Loader size={16} className="animate-spin" /> : <Check size={16} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
